import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BiBell } from 'react-icons/bi';
import { FaRegUser } from 'react-icons/fa';
import { LuLayoutDashboard } from 'react-icons/lu';
import { MdAdminPanelSettings, MdDashboard } from 'react-icons/md';
import NotificationBell from 'custom-notification-bell';
import { useState, useEffect } from 'react';

import logo from '../../../assets/logo/hplogo.jpg';
import homeMobileLogo from '../../../assets/logo/Hire-Panther_B-01-DMxqTKKG.png';
import { useAuth } from '../../../context/AuthContext';
import { handleLogin, handleRegistration } from '../auth/apiAuth';
import BecomeProviderModal from '../common/ui/BecomeProviderModal';
// import Language from '../common/Language';
// import Currency from '../common/Currency';
import { HeaderAuthDropdown } from '../HeaderAuthDropdown';
import { HeaderAuthMobileDropdown } from '../HeaderAuthMobileDropdown';
import { ROLE } from '../../../Role';
import { appConfig } from '../../../config/appConfig';
import { apiClient } from '../../../api';

const TopHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    isAuthenticated,
    role,
    userId,
    user,
    customerAccountStatus,
    providerAccountStatus,
    adminAccountStatus,
    loading: isAuthLoading,
  } = useAuth();

  const isHomePage = location.pathname.replace(/\/$/, '') === '/home';

  const showBecomeProvider = isAuthenticated && role === ROLE.CUSTOMER;

  // --- Dynamic Permissions Checks ---
  const isValidCustomer =
    (role === ROLE.CUSTOMER || role === ROLE.PROVIDER) &&
    customerAccountStatus &&
    ['active', 'deactivated'].includes(customerAccountStatus);

  const isValidProvider =
    role === ROLE.PROVIDER &&
    providerAccountStatus &&
    ['active', 'deactivated'].includes(providerAccountStatus);

  const isValidAdmin =
    role === ROLE.ADMIN &&
    adminAccountStatus &&
    ['active'].includes(adminAccountStatus);

  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const buildNotificationProps = () => ({
    websocketUrl: `${import.meta.env.VITE_APP_WEBSOCKET}`,
    apiUrl: `/notification`,
    bellIcon: <BiBell size={15} />,
    userId: `${userId}`,
    userCreatedAt: `${user?.createdAt}`,
    theme: 'dark' as const,
    showConnectionStatus: true,
    role: 'customer',
    appClient: apiClient,
  });

  return (
    <header
      className={`flex justify-between items-center px-4 md:px-6 laptop:px-14 py-2 font-primary text-xs md:text-sm border-b transition-colors duration-200 ${
        isHomePage
          ? 'bg-white text-gray-800 border-gray-200'
          : 'bg-gradient-to-tr from-[#2D617A] via-neutral-900 to-gray-950 text-white border-neutral-800'
      }`}
    >
      {/* Left Section: Branding Logo */}
      <div className="flex items-center gap-4">
        <Link to="/" className="transition-opacity hover:opacity-90">
          <img
            src={isHomePage ? homeMobileLogo : logo}
            alt={t('header.logo_alt')}
            className="object-contain h-9 md:h-12 w-auto"
          />
        </Link>
      </div>

      {/* Right Section: Navigation Links & Portal Actions */}
      <div className="flex items-center gap-3 md:gap-4 laptop:gap-6">
        {/* Desktop-only: BecomeProvider + Portal links */}
        <div className="hidden md:flex items-center gap-2">
          {showBecomeProvider && <BecomeProviderModal />}
        </div>

        <nav className="hidden md:flex items-center gap-1 px-2">
          {isValidCustomer && (
            <button
              onClick={() => navigate('/customer/customer-dashboard')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-normal transition ${
                isHomePage
                  ? 'hover:bg-sky-100 text-gray-700'
                  : 'hover:bg-white/10 text-gray-200'
              }`}
            >
              <MdDashboard className="text-sm" />
              <span>{t('header.customer_portal')}</span>
            </button>
          )}

          {isValidProvider && (
            <a
              href={appConfig.app.poviderPortalUrl}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-normal transition ${
                isHomePage
                  ? 'hover:bg-sky-100 text-gray-700'
                  : 'hover:bg-white/10 text-gray-200'
              }`}
            >
              <LuLayoutDashboard className="text-sm" />
              <span>{t('header.provider_portal')}</span>
            </a>
          )}

          {isValidAdmin && (
            <a
              href={appConfig.app.adminPortalUrl}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-normal transition ${
                isHomePage
                  ? 'hover:bg-sky-100 text-gray-700'
                  : 'hover:bg-white/10 text-gray-200'
              }`}
            >
              <MdAdminPanelSettings className="text-base" />
              <span>{t('header.admin_portal')}</span>
            </a>
          )}
        </nav>

        {/* Notification bell — only ONE, always visible when authenticated */}
        {/* Wrapped in a color-reset container to prevent header text-white
            from inheriting into the dropdown and making text invisible */}
        {isAuthenticated && (
          <div className="text-gray-900">
            <NotificationBell
              {...buildNotificationProps()}
              bellIcon={
                <span className={isHomePage ? 'text-gray-800' : 'text-white'}>
                  <BiBell size={15} />
                </span>
              }
              onNotificationClick={() =>
                navigate('/customer/customer-notifications')
              }
            />
          </div>
        )}

        {/* Auth: rendered with JS (not CSS) — only ONE at a time */}
        {!isAuthenticated ? (
          <div className="flex items-center gap-3 text-sm">
            {!isAuthLoading && (
              <>
                <button
                  onClick={() => handleLogin()}
                  className="flex items-center gap-1.5 text-xs font-normal hover:text-primary transition-colors"
                >
                  <FaRegUser className="text-[10px]" />
                  {t('header.login')}
                </button>
                <span className="h-3 w-px bg-gray-300/50"></span>
                <button
                  onClick={() => handleRegistration()}
                  className="flex items-center gap-1.5 text-xs font-normal hover:text-primary transition-colors"
                >
                  <FaRegUser className="text-[10px]" />
                  {t('header.register')}
                </button>
              </>
            )}
          </div>
        ) : isDesktop ? (
          <HeaderAuthDropdown />
        ) : (
          <HeaderAuthMobileDropdown />
        )}
      </div>
    </header>
  );
};

export default TopHeader;
